<script lang="ts">
  /**
   * ThisSwimSection — the per-swim charts for the swim on screen: heart rate,
   * stroke mix, glide by stroke, and what you fixed. Folded in from the old
   * Review tab, then moved WHOLE from the Scores/Sets tab to the bottom of the
   * Swim tab (Scott's Q1 ruling, 2026-09-10: the per-swim stroke charts stay
   * with the swim; Strokes is history across swims, and a one-swim chart there
   * brought back the "26s vs 27s" scope mix-up). Unchanged otherwise, with its
   * own calm empty state. Reads currentDerivedWorkout, which the Swim panel's
   * own engine (Prototypes) publishes.
   *
   * A div, not a section: app.css frames every `#app-root section[class]` as a
   * glass card, and this block is a stack of cards.
   */
  import { workouts } from "../lib/workoutsStore";
  import ReviewCard from "./review/ReviewCard.svelte";
  import { hrCardNote, strokeTimeHeadline, strokeTimeNote } from "../lib/openWaterCopy";
  import HrTimelineChart from "./review/HrTimelineChart.svelte";
  import StrokeMixChart from "./review/StrokeMixChart.svelte";
  import GlideByStrokeChart from "./review/GlideByStrokeChart.svelte";
  import CorrectionsCard from "./CorrectionsCard.svelte";
  import { correctionsHeadline } from "../lib/corrections";
  import { openOverlay } from "../lib/overlay";
  import {
    currentDerivedWorkout,
    currentRawFirehose,
    currentFirehoseStatus,
    offline,
  } from "../lib/state";
  import { formatWorkoutDay } from "../lib/format";
  import { reviewEmptyCopy } from "../lib/reviewEmpty";

  function openPickASwim() {
    openOverlay({ kind: "your-swims" });
  }

  let dw = $derived($currentDerivedWorkout);
  let owSwim = $derived(dw?.isOpenWater === true);
  let swimDate = $derived(
    $currentRawFirehose?.workout?.startDate
      ? formatWorkoutDay($currentRawFirehose.workout.startDate)
      : null,
  );
  let isLoading = $derived($currentFirehoseStatus === "loading");
  let hasSwim = $derived(dw !== null);
  let emptyCopy = $derived(reviewEmptyCopy($workouts.length, $offline === "health-off"));
</script>

<div class="this-swim" data-this-swim>
  <div class="swim-head">
    <h2 class="swim-title">This swim{swimDate ? ` · ${swimDate}` : ""}</h2>
    <button type="button" class="pick-btn" onclick={openPickASwim}>
      Pick a swim <span aria-hidden="true">›</span>
    </button>
  </div>

  {#if isLoading}
    <div class="empty-card">
      <p class="empty-state">Loading swim...</p>
    </div>
  {:else if !hasSwim}
    <div class="empty-card">
      <p class="empty-state">{emptyCopy}</p>
    </div>
  {:else}
    <ReviewCard headline="Heart rate, start to finish" note={hrCardNote(owSwim)}>
      <HrTimelineChart dw={dw!} />
    </ReviewCard>

    <ReviewCard headline="Stroke mix">
      <StrokeMixChart dw={dw!} />
    </ReviewCard>

    <!-- Note is a SCOPE label, not the moral (the "smoother" moral is said ONCE,
         in the Glide trend card on the Strokes tab). "This swim" scopes these
         per-length numbers so this swim's "26s" never reads as a typo against
         the Glide card's 12-swim-average "27 seconds" (persona gate round 4). -->
    <ReviewCard headline={strokeTimeHeadline(owSwim)} note={strokeTimeNote(owSwim)}>
      <GlideByStrokeChart dw={dw!} />
    </ReviewCard>

    {#if dw!.corrections.changed.length > 0 || dw!.corrections.unmatchedCount > 0}
      <ReviewCard
        headline={dw!.corrections.changed.length > 0
          ? correctionsHeadline(dw!.corrections.changed.length)
          : "What you fixed"}
      >
        <CorrectionsCard vm={dw!.corrections} dw={dw!} />
      </ReviewCard>
    {/if}
  {/if}
</div>

<style>
  .this-swim {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }
  .swim-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-2);
    margin: var(--space-2) 2px 0;
  }
  .swim-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
    line-height: 1.3;
  }
  .pick-btn {
    border: 0;
    background: transparent;
    padding: var(--space-2);
    margin: calc(-1 * var(--space-2)) calc(-1 * var(--space-2)) calc(-1 * var(--space-2)) 0;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    color: var(--accent-primary);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    min-height: 44px;
    /* On Swim the day reads "Today · Thursday, Sep 10": the title wraps, the
       button must not (it broke into "Pick a / swim ›" at 390). */
    white-space: nowrap;
    flex-shrink: 0;
  }
  .pick-btn:focus-visible {
    outline: 2px solid var(--accent-primary);
    outline-offset: 2px;
    border-radius: var(--radius-button);
  }

  /* Calm empty card — same family as the rest of the page. */
  .empty-card {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    background: var(--bg-card);
    border: 0;
    border-radius: var(--radius-card);
    padding: var(--space-6) var(--space-4);
    backdrop-filter: blur(22px) saturate(1.2);
    -webkit-backdrop-filter: blur(22px) saturate(1.2);
  }
  .empty-state {
    font-size: 17px;
    color: var(--text-muted);
    text-align: center;
    line-height: 1.45;
    margin: 0;
  }
</style>
